import React from 'react';
import { Box } from '@mui/material';
import { useUnderwriterContext } from '../../views/underwriter/provider/UnderwriterProvider';
import { isUnderwriterContext } from '../../views/underwriter/types';
import CarrierSelect from './carrier-select/CarrierSelect';
import GlobalInput from '../GlobalInput';
import GlobalsRow from './GlobalsRow';
import {
  UW_BORDERLESS_SELECT_SX,
  UW_GLOBAL_LABEL_STYLE,
} from '../../views/underwriter/components/uwTileSpacing';
import { UwSharedLimitWithCtx } from './SharedLimit';
import RetentionField from '../../views/underwriter/components/rater-x-components/global/RetentionField';
import NyftzSelect from './NyftzSelect';
import { AGGREGATE_LIMIT_LABEL } from './globalLabels';
import StagedWas from '../../views/underwriter/draft/components/StagedWas';
import { useStagedSx } from '../../views/underwriter/draft/components/useStagedSx';
import StagedRowActions from '../../views/underwriter/draft/components/StagedRowActions';
import {
  globalFieldPath,
  raterOptionPath,
} from '../../views/underwriter/draft/stagedFields';
import { BOUND } from '../../../../../_statics/binder.statics';
import { useFeature } from '@growthbook/growthbook-react';
import { FEATURE_FLAG_KEYS } from '../../../../../../config/growthbook';

interface RaterAggregateGlobalsProps {
  /** Product version, or the related-quotes list; sits before Carrier. */
  readonly leading?: React.ReactNode;
}

/** Each field's share of the row: wide enough for its label, free to shrink. */
const GLOBAL_FIELD_FLEX = '0 1 14rem';

const RaterAggregateGlobals = ({ leading }: RaterAggregateGlobalsProps) => {
  const context = useUnderwriterContext();
  const isUwQuoteState = isUnderwriterContext(context);
  const restrictUwCarrierChangeFeature = useFeature(
    FEATURE_FLAG_KEYS.RESTRICT_UW_CARRIER_CHANGE
  );

  // Carrier selection should be disabled for bound quotes (PRD-711)
  const isBound =
    isUwQuoteState && context.state.quote?.quote?.agencyStatus === BOUND;
  const globalProps = context.state.global?.value?.props || {};
  const globalRules = context.state.global?.rules || {};
  const nyftzClassCodeValue = globalProps.nyftzClassCode?.value;
  const nyftzClassCodeOptions = globalRules.nyftzClassCodeOptions
    ?.options as string[];
  const aggregateLimitValue = Number(globalProps.limit?.value) || 0;
  const aggregateLimitOptions = globalRules.limit?.options ?? [];
  const defenseLimitMin = Number(globalRules.defenseLimit?.from);
  const defenseLimitMax = Number(globalRules.defenseLimit?.to);
  const defenseLimitStep = globalRules.defenseLimit?.step;
  const defenseLimitValue = Number(globalProps.defenseLimit?.value) || 0;
  const defenseLimitInputMin = globalRules.defenseLimit?.from;
  const defenseLimitInputMax = globalRules.defenseLimit?.to;
  const defenseLimitInputStep = globalRules.defenseLimit?.step;

  const carrierSx = useStagedSx([raterOptionPath('carrier')]);
  const limitSx = useStagedSx([globalFieldPath('limit')]);
  const nyftzSx = useStagedSx([globalFieldPath('nyftzClassCode')]);
  const defenseSx = useStagedSx([globalFieldPath('defenseLimit')]);
  // The shared-limit cell owns two paths: the limit, and which components share
  // it. Either can stage on its own.
  const sharedLimitPaths = [
    globalFieldPath('sharedLimit'),
    globalFieldPath('sharedLimitComponents'),
  ];
  const sharedLimitSx = useStagedSx(sharedLimitPaths);

  const carrierField = isUwQuoteState ? (
    <Box width="12rem" flexShrink={0} sx={carrierSx}>
      <CarrierSelect
        labelStyle={UW_GLOBAL_LABEL_STYLE}
        value={context.state.raterOptions.carrier.value}
        options={context.state.raterOptions.carrier.options}
        onSelect={(treatyId) =>
          context.handlers.setRaterOption({
            id: 'carrier',
            value: treatyId,
          })
        }
        disabled={isBound && restrictUwCarrierChangeFeature.on}
        labelAdornment={
          <StagedRowActions paths={[raterOptionPath('carrier')]} />
        }
      />
      <StagedWas paths={[raterOptionPath('carrier')]} prefixed />
    </Box>
  ) : null;

  return (
    // Product version and Carrier lead on the left, as on the single-rater
    // row; the rest pack right. Each field takes the width its label needs
    // (the 1.5-unit cells truncated "Aggregate Limit of Liability") and gives
    // it back on a narrow window.
    <GlobalsRow
      leading={
        <>
          {leading}
          {carrierField}
        </>
      }
    >
      {nyftzClassCodeValue && (
        <Box flex={GLOBAL_FIELD_FLEX} minWidth={0} sx={nyftzSx}>
          <GlobalInput
            selectProps={{ sx: UW_BORDERLESS_SELECT_SX }}
            labelStyle={UW_GLOBAL_LABEL_STYLE}
            label="NYFTZ Class Code"
            labelAdornment={
              <StagedRowActions paths={[globalFieldPath('nyftzClassCode')]} />
            }
          >
            <NyftzSelect
              name="nyftzClassCode"
              value={nyftzClassCodeValue}
              options={nyftzClassCodeOptions}
              setGlobalValue={context.handlers.setGlobalValue}
            />
          </GlobalInput>
          <StagedWas paths={[globalFieldPath('nyftzClassCode')]} prefixed />
        </Box>
      )}
      <Box flex={GLOBAL_FIELD_FLEX} minWidth={0} sx={limitSx}>
        <GlobalInput
          selectProps={{ sx: UW_BORDERLESS_SELECT_SX }}
          labelStyle={UW_GLOBAL_LABEL_STYLE}
          label={AGGREGATE_LIMIT_LABEL}
          name="limit"
          setGlobalValue={context.handlers.setGlobalValue}
          value={aggregateLimitValue}
          options={aggregateLimitOptions}
          labelAdornment={
            <StagedRowActions paths={[globalFieldPath('limit')]} />
          }
        />
        <StagedWas paths={[globalFieldPath('limit')]} prefixed />
      </Box>
      <Box flex={GLOBAL_FIELD_FLEX} minWidth={0} sx={sharedLimitSx}>
        {/* No `GlobalInput` here to hang the pair off a label, so it sits above
            the control — the nearest thing this cell has to one. */}
        <StagedRowActions paths={sharedLimitPaths} />
        <UwSharedLimitWithCtx />
        <StagedWas paths={sharedLimitPaths} prefixed />
      </Box>

      <Box flex={GLOBAL_FIELD_FLEX} minWidth={0} sx={defenseSx}>
        <GlobalInput
          selectProps={{ sx: UW_BORDERLESS_SELECT_SX }}
          labelStyle={UW_GLOBAL_LABEL_STYLE}
          label="Add't Defense Cost Limit"
          labelAdornment={
            <StagedRowActions paths={[globalFieldPath('defenseLimit')]} />
          }
        >
          <RetentionField
            variant="outlined"
            size="small"
            name="defenseLimit"
            min={defenseLimitMin}
            max={defenseLimitMax}
            step={defenseLimitStep}
            value={defenseLimitValue}
            onChange={context.handlers.setGlobalValue}
            inputProps={{
              min: defenseLimitInputMin,
              max: defenseLimitInputMax,
              step: defenseLimitInputStep,
            }}
            sx={{
              width: '100%',
            }}
          />
        </GlobalInput>
        <StagedWas paths={[globalFieldPath('defenseLimit')]} prefixed />
      </Box>
    </GlobalsRow>
  );
};

export default RaterAggregateGlobals;
